import { create } from 'zustand';

/**
 * In-progress SOAP synth jobs surfaced app-wide so the doctor isn't
 * held hostage to the dock or the review page while synth runs.
 *
 * Lifecycle:
 *   1. RecordingDock.handleComplete (or EncounterReviewPage regenerate)
 *      calls `register({ ... })` and immediately navigates the doctor
 *      anywhere they want — start a new consult, browse the schedule,
 *      sign off the day.
 *   2. The shell-mounted `SynthEventsBridge` listens to scribe.* events
 *      and routes stage updates / status messages / completion / errors
 *      into this store, scoped by sessionId.
 *   3. The shell-mounted `SynthBackgroundPill` shows one pill per
 *      active job (count + most-recent stage label). Clicking the pill
 *      opens the dedicated review page for that job.
 *
 * Jobs are kept until the doctor dismisses them (completed / errored
 * jobs auto-toast and stick around in the pill list for ~60 s so the
 * doctor sees the win/failure without losing it on a route change).
 */

export type SynthJobStatus = 'running' | 'completed' | 'failed';

export type SynthStageStatus = 'pending' | 'active' | 'done' | 'failed';

export interface SynthJobStage {
  id: string;
  status: SynthStageStatus;
  detail?: string;
}

export interface SynthJob {
  /**
   * Stable client-side id. Use the `recordingSessionId` for first-time
   * gen, or `regen:<recordId>:<startedAt>` for regenerate so multiple
   * back-to-back regenerates don't collide.
   */
  id: string;
  /** Session id the gateway tags its events with — used to filter the
   *  bridge subscription. Null when the regen path doesn't expose one. */
  sessionId: string | null;
  /** Patient name for the pill label. */
  patientName: string;
  patientId?: string | null;
  /** Filled when synth completes — used by the pill click target. */
  recordId?: string | null;
  /** ms epoch */
  startedAt: number;
  /** ms epoch — set when status flips to completed or failed. */
  finishedAt?: number;
  status: SynthJobStatus;
  /** Doctor-friendly status message currently being shown. */
  statusLabel: string | null;
  /** Per-stage state, keyed by stage id (extract / schema / verify / …). */
  stages: Record<string, SynthJobStage>;
  /** Latest transcript the AI is reading from, live or refined. */
  transcript: string | null;
  transcriptIsRefined: boolean;
  /** Failure reason for `status: 'failed'`. */
  errorMessage?: string;
  /** "What kind of work" — controls the pill label. */
  kind: 'generate' | 'regenerate';
}

export interface RegisterSynthJobInit {
  id: string;
  sessionId: string | null;
  patientName: string;
  patientId?: string | null;
  recordId?: string | null;
  kind?: 'generate' | 'regenerate';
}

interface SynthJobsState {
  jobs: Record<string, SynthJob>;

  register(init: RegisterSynthJobInit): void;
  setStage(
    id: string,
    stage: { id: string; status: SynthStageStatus; detail?: string },
  ): void;
  setStatusLabel(id: string, label: string | null): void;
  setTranscript(id: string, transcript: string, refined: boolean): void;
  complete(
    id: string,
    payload: { recordId?: string | null; patientId?: string | null },
  ): void;
  fail(id: string, message: string): void;
  dismiss(id: string): void;
}

export const useSynthJobsStore = create<SynthJobsState>((set) => ({
  jobs: {},

  register(init) {
    set((state) => ({
      jobs: {
        ...state.jobs,
        [init.id]: {
          id: init.id,
          sessionId: init.sessionId,
          patientName: init.patientName,
          patientId: init.patientId ?? null,
          recordId: init.recordId ?? null,
          startedAt: Date.now(),
          status: 'running',
          statusLabel: null,
          stages: {},
          transcript: null,
          transcriptIsRefined: false,
          kind: init.kind ?? 'generate',
        },
      },
    }));
  },

  setStage(id, stage) {
    set((state) => {
      const job = state.jobs[id];
      if (!job) return state;
      return {
        jobs: {
          ...state.jobs,
          [id]: {
            ...job,
            stages: {
              ...job.stages,
              [stage.id]: {
                id: stage.id,
                status: stage.status,
                detail: stage.detail,
              },
            },
          },
        },
      };
    });
  },

  setStatusLabel(id, label) {
    set((state) => {
      const job = state.jobs[id];
      if (!job) return state;
      return {
        jobs: { ...state.jobs, [id]: { ...job, statusLabel: label } },
      };
    });
  },

  setTranscript(id, transcript, refined) {
    set((state) => {
      const job = state.jobs[id];
      if (!job) return state;
      return {
        jobs: {
          ...state.jobs,
          [id]: { ...job, transcript, transcriptIsRefined: refined },
        },
      };
    });
  },

  complete(id, payload) {
    set((state) => {
      const job = state.jobs[id];
      if (!job) return state;
      // Mark anything still pending/active as done so the pill doesn't
      // freeze mid-progress when the backend skipped a stage event.
      const stages: Record<string, SynthJobStage> = {};
      for (const [key, value] of Object.entries(job.stages)) {
        stages[key] =
          value.status === 'pending' || value.status === 'active'
            ? { ...value, status: 'done' }
            : value;
      }
      return {
        jobs: {
          ...state.jobs,
          [id]: {
            ...job,
            status: 'completed',
            finishedAt: Date.now(),
            recordId: payload.recordId ?? job.recordId ?? null,
            patientId: payload.patientId ?? job.patientId ?? null,
            stages,
          },
        },
      };
    });
  },

  fail(id, message) {
    set((state) => {
      const job = state.jobs[id];
      if (!job) return state;
      return {
        jobs: {
          ...state.jobs,
          [id]: {
            ...job,
            status: 'failed',
            finishedAt: Date.now(),
            errorMessage: message,
          },
        },
      };
    });
  },

  dismiss(id) {
    set((state) => {
      const next = { ...state.jobs };
      delete next[id];
      return { jobs: next };
    });
  },
}));

/**
 * Selector helpers — callers can subscribe narrowly to avoid noisy renders.
 *
 * IMPORTANT: `selectActiveSynthJobs` and `selectAllSynthJobs` return a fresh
 * array reference every call (`Object.values(...)` + `.filter`/`.sort`). Pass
 * them through `useShallow` from `zustand/react/shallow` when subscribing,
 * or React 18's useSyncExternalStore cached-snapshot check fails and the
 * component infinite-loops with "Maximum update depth exceeded" (#185).
 *
 *   const jobs = useSynthJobsStore(useShallow(selectAllSynthJobs)); // ✅
 *   const jobs = useSynthJobsStore(selectAllSynthJobs);              // 💥
 *
 * Reading via `useSynthJobsStore.getState()` (no subscription) is also safe.
 * `selectSynthJob(id)` returns a stable reference (the row itself), so it
 * does NOT need useShallow.
 */
export const selectActiveSynthJobs = (state: { jobs: Record<string, SynthJob> }) =>
  Object.values(state.jobs).filter((j) => j.status === 'running');

export const selectAllSynthJobs = (state: { jobs: Record<string, SynthJob> }) =>
  Object.values(state.jobs).sort((a, b) => b.startedAt - a.startedAt);

export const selectSynthJob = (id: string | null | undefined) =>
  (state: { jobs: Record<string, SynthJob> }) =>
    id ? (state.jobs[id] ?? null) : null;
